import { booleanAttribute, Component, computed, Injector, inject, input } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, EMPTY, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { ChatCreateDialogComponent, IChatCreateDialogData } from '../chat-create-dialog/chat-create-dialog.component';
import { ChatListService } from '../../services/chat-list.service';
import { ChatPageService } from '../../services/chat-page.service';
import { ChatPollingService } from '../../services/chat-polling.service';
import { IChatDto, chatDto } from 'src/assets/common/models/chat-dto.g';
import { IChatListDto } from 'src/assets/common/models/chat-list-dto.g';
import { IChatListData, chatListData } from '../../models/chat-list-data';
import { AppDatePipe } from 'src/app/infrastructure/pipes/app-date.pipe';
import { ExpansionList_ToolComponent } from 'src/app/features/shared/lists/expansion-list/container/expansion-list-tool/expansion-list.component';
import { FilterService } from 'src/app/features/shared/filter/filter.service';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { LIST_KIND_NAME, _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { _NavigationList_Base_FeatureComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-feature.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { claimPage } from 'src/app/infrastructure/models/claim-page';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';

// Chatlijst, drie plekken (gekozen met de inputs mine/task, niet via de actieve tab: een niet-actieve tab kan ook herladen):
//   - Project › chats (listFromProject): de chats van het project zelf (zonder taak); toevoegknop = nieuwe chat (dialoog).
//   - Taak › chats (listFromTask, key = de taak op de pagina): de chats van die taak; een nieuwe chat hoort bij de taak.
//   - Home › chats (mine): het centrale overzicht, open chats van de gebruiker over alle domains; regel 1 noemt
//     dan ook het domain en het project.
// Rij: onderwerp, laatste bericht met afzender, tijd en het aantal ongelezen. Afgerond = gedimd (inactive).
@Component({
    selector: 'app-chat-tablist',
    templateUrl: './chat-tablist.component.html',
    styleUrls: [
        '../../../shared/central-page/central-page.scss',
        '../../../shared/central-page/container/tablist/tablist.scss',
        '../../../shared/lists/title-subline-row.scss',
        './chat-tablist.component.scss',
    ],
    standalone: true,
    imports: [
        AppDatePipe,
        ExpansionList_ToolComponent,
        SharedModule,
    ],
    providers: [
        {provide: LIST_KIND_NAME, useValue: tabName.projectChats},
        TabListStorageService,
        TabDataService,
        PagerService,
        {provide: FilterService, useExisting: PagerService},

        {provide: _PageService, useExisting: ChatPageService},
        {provide: _ListService, useExisting: ChatListService},
    ],
    animations: [
        showAnimation
    ]
})
export class ChatTablistComponent
    extends _NavigationList_Base_FeatureComponent<IChatListDto, IChatListData>
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __chatPageService = injectRequired(ChatPageService);
    private readonly __chatPollingService = injectRequired(ChatPollingService);
    private readonly __injector = inject(Injector);
    private readonly __matDialog = injectRequired(MatDialog);

    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageSize$$ = this._input_pageSize$$;

    override out_onClick = this._out_onClick;
    override out_onPageLoaded = this._out_onPageLoaded;
    override out_onSave = this._out_onSave;

    override btnAddData$$ = this._input_btnAddData$$;
    override btnDeleteData$$ = this._input_btnDeleteData$$;
    override buttonsHide$$ = this._input_buttonsHide$$;
    override httpParams$$ = this._computed_httpParams$$;
    override list$$ = this._computed_list$$;
    override noClick$$ = this._input_noClick$$;
    override pageSize$$ = this._input_pageSize$$;
    override requestName$$ = this._computed_requestName$$;
    override tabIndex$$ = this._computed_tabIndex$$;

    // Centraal overzicht (Home) of de project-tab: bepaalt de request, de claimpagina en regel 1.
    readonly isMine$$ = input(false, { transform: booleanAttribute, alias: 'mine' });
    readonly isTask$$ = input(false, { transform: booleanAttribute, alias: 'task' });
    override claimPageList$$ = computed(() => this.isMine$$() ? claimPage.homeChats : this.isTask$$() ? claimPage.taskChats : claimPage.projectChats);
    // Regel 1: (domain › project ·) tijd; titel: onderwerp; regel 3: laatste bericht.
    override itemLines = 3;

    subject = chatListData.subject;
    lastMessageOn = chatListData.lastMessageOn;
    unreadCount = chatListData.unreadCount;
    closed = chatListData.closed;

    text = {
        context_separator: ' › ',
        time_separator: ' · ',
        sender_separator: ': ',
        closed: 'chat gesloten',
        unread: 'ongelezen berichten',
    };

    context_text(item: IChatListData | null): string {
        if (!item || !this.isMine$$()) return '';
        return [item[chatListData.domain_name], item[chatListData.project_name], item[chatListData.task_subject]]
            .filter(x => !!x).join(this.text.context_separator);
    }

    lastMessage_text(item: IChatListData | null): string {
        if (!item?.[chatListData.lastMessage_body]) return '';
        const __sender = item[chatListData.lastMessage_senderName];
        return __sender ? `${__sender}${this.text.sender_separator}${item[chatListData.lastMessage_body]}` : item[chatListData.lastMessage_body]!;
    }

    override ngOnInit(): void {
        this._ngOnInit();
    }

    // Nieuwe chat: dialoog met onderwerp, deelnemers en eerste bericht; daarna meteen de chat openen.
    override emit_btnAddClick(): void {
        const __data: IChatCreateDialogData = {
            projectAMemberGuid: this._activatedRouteService.routeKeys?.pamGuid ?? undefined,
            taskGuid: this.isTask$$() ? this._activatedRouteService.routeKeys?.key ?? undefined : undefined,
        };
        this.__matDialog
            // Injector van de pagina: controls in de dialoog kunnen pagina-services vragen (zie NoticePartyDialog).
            .open<ChatCreateDialogComponent, IChatCreateDialogData, IChatDto>(ChatCreateDialogComponent, { data: __data, autoFocus: 'first-tabbable', injector: this.__injector })
            .afterClosed()
            .subscribe(dto => {
                if (!dto?.[chatDto.guid]) return;
                this._tabListService.tabList_setRefresh(true);
                this.__chatPageService.pageChat_goto(dto[chatDto.guid]!, this._activatedRouteService.routeKeys?.dauGuid ?? null);
            });
    }

    override emit_onClick(item: ILevelGeneralListData): void {
        this.__chatPageService.pageRoute_goto(item as IChatListData);
    }

    // Chats worden niet verwijderd, dus ook niet teruggezet.
    override emit_onRestore(_restoreList: any[]): void {
    }

    protected override _list_get$(): Observable<IChatListDto[]> {
        // Een lijst laden is ook een goed moment om de badge bij te werken (de tellers kunnen net veranderd zijn).
        this.__chatPollingService.refresh();

        if (this.isMine$$()) {
            return from(this.__apiService.GET('/api/Chat/mine', {
                params: { query: { includeDone: false } }
            })).pipe(map(r => (r.data as any)?.list as IChatListDto[] ?? []));
        }

        const __routeKeys = this._activatedRouteService.routeKeys;
        const __request = this.isTask$$()
            ? this.__apiService.GET('/api/Chat/list-from-task', {
                params: { query: { key: __routeKeys?.key ?? undefined, projectAMemberGuid: __routeKeys?.pamGuid ?? undefined } }
            })
            : this.__apiService.GET('/api/Chat/list-from-project', {
                params: { query: { projectAMemberGuid: __routeKeys?.pamGuid ?? undefined } }
            });
        return from(__request).pipe(map(r => (r.data as any)?.list as IChatListDto[] ?? []));
    }

    protected override _restore_patch$(_restoreList: any[], _httpParams: HttpParams | null): Observable<IChatListDto> {
        return EMPTY;
    }
}
